import React, { useEffect, useState } from "react";
import { Hash, Save, Loader2, Receipt, Megaphone, CalendarClock, Zap, Copy, Check, Lock } from "lucide-react";
import ProfileSettingsCard from "../common/ProfileSettingsCard";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { fetchSettings, updateSettings, authChangePassword } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ChangePasswordCard() {
  const { token } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    setSaving(true);
    try {
      await authChangePassword(token, current, next);
      toast.success("Password changed successfully!");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="crm-change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="h-5 w-5 text-gold-dark" />
        <h2 className="font-serif text-lg font-semibold text-navy">Password</h2>
      </div>
      <div className="space-y-3 max-w-md">
        <div>
          <label className="text-sm font-medium text-gray-700">Current password</label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5" data-testid="crm-password-current" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">New password</label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="min. 6 characters" className="mt-1.5" data-testid="crm-password-new" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Confirm new password</label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="crm-password-confirm" />
        </div>
        <Button onClick={submit} disabled={saving} className="bg-navy hover:bg-navy-light text-white font-semibold" data-testid="crm-password-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Change Password</>)}
        </Button>
      </div>
    </div>
  );
}

function DisputeFoxSettings() {
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        setEnabled(!!s.disputefox_enabled);
        setUrl(s.disputefox_webhook_url || "");
        setSecret(s.disputefox_inbound_secret || "");
      })
      .catch(() => {});
  }, []);

  const inboundUrl = secret
    ? `${BACKEND}/api/integrations/disputefox/webhook?secret=${secret}`
    : "";

  const copyInbound = () => {
    if (!inboundUrl) return;
    navigator.clipboard.writeText(inboundUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("Inbound webhook URL copied");
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ disputefox_enabled: enabled, disputefox_webhook_url: url.trim() });
      toast.success("DisputeFox integration saved.");
    } catch (e) {
      toast.error("Could not save the DisputeFox settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="disputefox-settings-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-orange-50 flex items-center justify-center">
          <Zap className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-navy">DisputeFox Integration</h2>
          <p className="text-sm text-gray-500">Two-way sync of leads & clients with your DisputeFox account.</p>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between max-w-lg">
        <div>
          <div className="text-sm font-medium text-gray-700">Enable sync</div>
          <div className="text-xs text-gray-400">Push new leads & clients to DisputeFox automatically.</div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="disputefox-enabled-switch" />
      </div>

      <div className="mt-5 max-w-lg">
        <label className="text-sm font-medium text-gray-700">Outbound webhook URL (DisputeFox / Zapier)</label>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          className="mt-1.5"
          data-testid="disputefox-url-input"
        />
        <p className="text-xs text-gray-400 mt-1.5">
          When a new lead or client is created here, we POST their details to this URL.
        </p>
      </div>

      <div className="mt-5 max-w-lg">
        <label className="text-sm font-medium text-gray-700">Inbound webhook URL (give this to DisputeFox)</label>
        <div className="mt-1.5 flex gap-2">
          <Input value={inboundUrl} readOnly className="font-mono text-xs" data-testid="disputefox-inbound-url" />
          <Button type="button" variant="outline" onClick={copyInbound} className="flex-shrink-0" data-testid="disputefox-copy-inbound">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          Configure DisputeFox/Zapier to POST status changes to this URL. Matched clients get an activity entry.
        </p>
      </div>

      <Button onClick={save} disabled={saving} className="mt-5 bg-navy hover:bg-navy-light text-white font-semibold" data-testid="disputefox-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save Integration</>)}
      </Button>
    </div>
  );
}

function MarqueeSettings() {
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        setEnabled(!!s.marquee_enabled);
        setText(s.marquee_text || "");
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ marquee_enabled: enabled, marquee_text: text });
      toast.success("Marquee updated! Refresh the website to see changes.");
    } catch (e) {
      toast.error("Could not save the marquee.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="marquee-settings-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-cream flex items-center justify-center">
          <Megaphone className="h-5 w-5 text-gold-dark" />
        </div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-navy">Website Marquee</h2>
          <p className="text-sm text-gray-500">A scrolling announcement bar at the top of your public site.</p>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between max-w-lg">
        <div>
          <div className="text-sm font-medium text-gray-700">Show marquee</div>
          <div className="text-xs text-gray-400">Toggle the announcement bar on or off.</div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="marquee-enabled-switch" />
      </div>

      <div className="mt-5 max-w-lg">
        <label className="text-sm font-medium text-gray-700">Marquee text</label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Free credit consultation this month — book today!\nAsk us how you can increase your income!"}
          rows={3}
          className="mt-1.5"
          data-testid="marquee-text-input"
        />
        <p className="text-xs text-gray-400 mt-1.5">One message per line. Add a link with <span className="font-mono text-gray-500">Message | /contact</span> or <span className="font-mono text-gray-500">Message | https://…</span></p>
      </div>

      {enabled && text.trim() && (
        <div className="mt-4 max-w-lg rounded-lg overflow-hidden border border-gray-100">
          <div className="bg-navy-deep text-cream text-sm px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis">
            <span className="text-gold mr-3">◆</span>{text}
          </div>
          <div className="text-xs text-gray-400 px-4 py-1.5 bg-cream">Live preview</div>
        </div>
      )}

      <Button onClick={save} disabled={saving} className="mt-4 bg-navy hover:bg-navy-light text-white font-semibold" data-testid="marquee-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save Marquee</>)}
      </Button>
    </div>
  );
}

const DAYS = [
  { i: 0, label: "Sun" }, { i: 1, label: "Mon" }, { i: 2, label: "Tue" },
  { i: 3, label: "Wed" }, { i: 4, label: "Thu" }, { i: 5, label: "Fri" }, { i: 6, label: "Sat" },
];

function AvailabilitySettings() {
  const [days, setDays] = useState([1, 2, 3, 4, 5]);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [slot, setSlot] = useState(30);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        setDays(s.avail_days || [1, 2, 3, 4, 5]);
        setStart(s.avail_start || "09:00");
        setEnd(s.avail_end || "17:00");
        setSlot(s.slot_minutes || 30);
      })
      .catch(() => {});
  }, []);

  const toggleDay = (i) =>
    setDays((prev) => (prev.includes(i) ? prev.filter((d) => d !== i) : [...prev, i].sort()));

  const save = async () => {
    if (days.length === 0) {
      toast.error("Select at least one available day.");
      return;
    }
    if (start >= end) {
      toast.error("End time must be after start time.");
      return;
    }
    setSaving(true);
    try {
      await updateSettings({ avail_days: days, avail_start: start, avail_end: end, slot_minutes: Number(slot) });
      toast.success("Availability updated!");
    } catch (e) {
      toast.error("Could not save availability.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="availability-settings-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-cream flex items-center justify-center">
          <CalendarClock className="h-5 w-5 text-gold-dark" />
        </div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-navy">Appointment Availability</h2>
          <p className="text-sm text-gray-500">Set the days and times clients can book online.</p>
        </div>
      </div>

      <div className="mt-6">
        <label className="text-sm font-medium text-gray-700">Available days</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button
              key={d.i}
              type="button"
              onClick={() => toggleDay(d.i)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                days.includes(d.i) ? "bg-navy text-white" : "bg-cream text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`avail-day-${d.i}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-lg">
        <div>
          <label className="text-sm font-medium text-gray-700">Start time</label>
          <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5" data-testid="avail-start-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">End time</label>
          <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5" data-testid="avail-end-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Slot length</label>
          <Select value={String(slot)} onValueChange={(v) => setSlot(Number(v))}>
            <SelectTrigger className="mt-1.5" data-testid="avail-slot-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[15, 30, 45, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="mt-5 bg-navy hover:bg-navy-light text-white font-semibold" data-testid="availability-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save Availability</>)}
      </Button>
    </div>
  );
}

function InvoicingSettings() {
  const [settings, setSettings] = useState(null);
  const [start, setStart] = useState("");
  const [prefix, setPrefix] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings()
      .then((s) => {
        setSettings(s);
        setStart(String(s.invoice_start ?? 1001));
        setPrefix(s.invoice_prefix ?? "INV-");
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    const num = parseInt(start, 10);
    if (isNaN(num) || num < 1) {
      toast.error("Enter a valid starting number (1 or greater).");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSettings({ invoice_start: num, invoice_prefix: prefix || "INV-" });
      setSettings(updated);
      toast.success("Invoicing settings saved!");
    } catch (e) {
      toast.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-cream flex items-center justify-center">
          <Receipt className="h-5 w-5 text-gold-dark" />
        </div>
        <div>
          <h2 className="font-serif text-lg font-semibold text-navy">Invoicing</h2>
          <p className="text-sm text-gray-500">Set the starting number for new invoices.</p>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <label className="text-sm font-medium text-gray-700">Invoice Prefix</label>
          <Input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="INV-"
            className="mt-1.5"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Starting Invoice Number</label>
          <div className="relative mt-1.5">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="number"
              min="1"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="1001"
              className="pl-9"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 bg-cream rounded-lg px-4 py-3 text-sm text-gray-600 max-w-lg">
        Next invoice will be numbered{" "}
        <span className="font-semibold text-navy">
          {(prefix || "INV-")}
          {Number.isNaN(parseInt(start, 10)) ? "1001" : parseInt(start, 10) + (settings?.existing_count || 0)}
        </span>
        . Existing invoices keep their numbers.
      </div>

      <Button onClick={save} disabled={saving} className="mt-4 bg-navy hover:bg-navy-light text-white font-semibold">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Save Invoicing Settings</>)}
      </Button>
    </div>
  );
}

export default function CrmSettings() {
  const { user, updateProfile } = useAuth();

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Manage your profile and invoicing preferences.
        </p>
      </div>
      <ProfileSettingsCard user={user} role={user?.role} onSave={updateProfile} />
      <ChangePasswordCard />
      <DisputeFoxSettings />
      <MarqueeSettings />
      <AvailabilitySettings />
      <InvoicingSettings />
    </div>
  );
}
